/* -----------------------------------
Copyright: Logical Developments 2025.
Project:   ConNote Portal
Filename:  Addresses.js
Author:    jdkohl
Version:   0.01
Description:
Reusable address management component for vendor workflows.
Handles displaying, adding, and removing addresses from a vendor's whitelist.
Can be used in both AddVendor and EditVendor workflows.

History:
0.01  10-12-25 JDK   Created from EditVendor.js address management logic.
----------------------------------- */

import { useState } from 'react';

function Addresses({ vendor, myAddresses, onChange, readOnly = false }) {
  const [selectedAssignedId, setSelectedAssignedId] = useState(null);
  const [selectedAvailableId, setSelectedAvailableId] = useState('');

  // Resolve vendor's address IDs to full objects
  const assignedAddresses = vendor?.addresses
    ?.map(addressId => myAddresses?.find(addr => addr.id === addressId))
    .filter(addr => addr !== undefined) || [];

  // Get addresses not yet assigned
  const availableAddresses = myAddresses?.filter(
    addr => !vendor?.addresses?.includes(addr.id)
  ) || [];

  /**
   * Remove an address from the vendor's whitelist
   */
  const handleRemoveAddress = () => {
    if (selectedAssignedId !== null && !readOnly) {
      const updatedAddresses = vendor.addresses.filter(
        id => id !== selectedAssignedId
      );
      onChange(updatedAddresses);
      setSelectedAssignedId(null);
    }
  };

  /**
   * Add an address to the vendor's whitelist
   */
  const handleAddAddress = () => {
    if (selectedAvailableId && !readOnly) {
      const addressId = parseInt(selectedAvailableId);
      const updatedAddresses = [...vendor.addresses, addressId];
      onChange(updatedAddresses);
      setSelectedAvailableId('');
    }
  };

  /**
   * Format address object into a single display string
   * @param {Object} address - Address object with name, street, suburb, state, postcode
   * @returns {string} Formatted address string with space-separated parts
   */
  const formatAddress = (address) => {
    if (!address) return '';
    const parts = [
      address.name,
      address.street,
      address.suburb,
      address.state,
      address.postcode
    ].filter(Boolean);
    return parts.join(' ');
  };

  return (
    <div className="stack">
      {/* Assigned Addresses Section */}
      <div className="stack margin-block-start" title="Your vendor can send freight to the addresses you supply below.">
        <p className='h3 table-heading' >Vendor Delivery Addresses</p>
        <div className='stack my-users' style={{ minBlockSize: "20ch", maxBlockSize: "30ch" }}>
          {assignedAddresses.length === 0 ? (
            <div style={{ padding: "1rem", textAlign: "center", color: "var(--clr-neutral-700)" }}>
              No delivery addresses assigned
            </div>
          ) : (
            assignedAddresses.map((address) => (
              <button
                type="button"
                key={`assigned_addr_${address.id}`}
                className={selectedAssignedId === address.id ? 'checked btn' : 'not clicked btn'}
                onClick={() => !readOnly && setSelectedAssignedId(address.id)}
                disabled={readOnly}
              >
                {formatAddress(address)}
              </button>
            ))
          )}
        </div>
        {!readOnly && (
          <div className="cluster cluster-end">
            <button
              title="Remove Address"
              className={`btn ${!selectedAssignedId && "disabled"}`}
              type='button'
              onClick={handleRemoveAddress}
              disabled={!selectedAssignedId}
            >
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Add Address Section */}
      {!readOnly && (
        <div>
          <p>Select an address to add to the white list above:</p>
          <div className="cluster">
            <select
              value={selectedAvailableId}
              onChange={(e) => setSelectedAvailableId(e.target.value)}
              disabled={availableAddresses.length === 0}
              className="flex-grow"
            >
              <option value="">
                {availableAddresses.length === 0 ? 'No available addresses' : 'Select an address...'}
              </option>
              {availableAddresses.map((address) => (
                <option key={`available_addr_${address.id}`} value={address.id}>
                  {formatAddress(address)}
                </option>
              ))}
            </select>
            <button
              title="Add Address"
              className={`btn ${!selectedAvailableId && "disabled"}`}
              type='button'
              onClick={handleAddAddress}
              disabled={!selectedAvailableId}
            >
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Addresses;
